import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  CircularProgress,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  MarkEmailRead as MailIcon,
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, refreshUser } = useAuth();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    const q = searchParams.get('email') || '';
    if (q) {
      setEmail(decodeURIComponent(q));
    }
  }, [searchParams]);

  const from = location.state?.from || '/';

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');

    const trimmedEmail = email.trim();
    const digits = code.replace(/\D/g, '');
    if (!trimmedEmail) {
      setError('Please enter your email address');
      return;
    }
    if (digits.length !== 6) {
      setError('Enter the 6-digit code from your email');
      return;
    }

    setLoading(true);
    try {
      await authAPI.verifyEmail({ email: trimmedEmail, code: digits });
      if (isAuthenticated) {
        await refreshUser();
        navigate(from, { replace: true });
      } else {
        setInfo('Email verified. You can log in now.');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }
    } catch (err) {
      setError(err.message || 'Verification failed. Check the code or request a new one.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!isAuthenticated) {
      setError('Log in to resend the code to your email.');
      return;
    }
    setError('');
    setInfo('');
    setResendLoading(true);
    try {
      await authAPI.resendEmailVerification();
      setInfo('We sent a new verification code to your email address.');
    } catch (err) {
      setError(err.message || 'Could not resend. Try again later.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {!isMobile && (
        <Box
          sx={{
            width: '45%',
            maxWidth: 560,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: 6,
            py: 4,
          }}
        >
          <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>
            Verify your email
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
            Enter the 6-digit code we sent. Codes expire after a short time; you can request a new one while logged in.
          </Typography>
        </Box>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton
            onClick={() => navigate(isAuthenticated ? from : '/login')}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <BackIcon />
          </IconButton>
          {!isAuthenticated && (
            <Link component={RouterLink} to="/login" variant="body2" fontWeight={600} underline="hover">
              Log in
            </Link>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 3, sm: 4 },
            py: 4,
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              Email verification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the code from your inbox to verify this address.
            </Typography>

            {error && (
              <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 1.5 }}>
                {error}
              </Alert>
            )}
            {info && (
              <Alert severity="success" onClose={() => setInfo('')} sx={{ mb: 2, borderRadius: 1.5 }}>
                {info}
              </Alert>
            )}

            <Box component="form" onSubmit={handleVerify}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  InputProps={{
                    startAdornment: <MailIcon fontSize="small" color="action" sx={{ mr: 1 }} />,
                  }}
                />
                <TextField
                  fullWidth
                  label="6-digit code"
                  inputMode="numeric"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  helperText="Check your email for the verification code"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ py: 1.4, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify email'}
                </Button>
                {isAuthenticated && (
                  <Button
                    type="button"
                    variant="outlined"
                    fullWidth
                    disabled={resendLoading || loading}
                    onClick={handleResend}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    {resendLoading ? <CircularProgress size={22} /> : 'Resend code'}
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VerifyEmail;
